import useRequest from "@/lib/request/useRequest";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { Listbox, ListboxItem, Selection, Spinner } from "@heroui/react";
import {
    BarChart,
    Bar,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { Weather } from "@/types/weather";
import { FieldConfig } from "@/types/fieldConfig";
import dayjs from "dayjs";

function WeatherView({ field }: { field: FieldConfig }) {
    const { useGetRequest } = useRequest();
    const yearOptions: string[] = useMemo(() => {
        const currentYear = dayjs().year();
        const years = [];
        for (let i = 0; i < 10; i++) {
            years.push((currentYear - i).toString());
        }
        years.push("Decadal Mean");
        return years;
    }, []);

    const [selectedWeatherYears, setSelectedWeatherYears] = useState<Selection>(
        new Set([dayjs().year().toString()]),
    );

    const { t } = useTranslation("insights");

    const { formatUnit } = useI18n();

    const tenYearWeather = useGetRequest<Weather[]>({
        url: `/fields/${field.id}/ten_year_weather/`,
        enabled: Array.from(selectedWeatherYears).includes("Decadal Mean"),
    });

    const [startDate, endDate] = useMemo(() => {
        const selectedYears = Array.from(selectedWeatherYears)
            .filter((year) => year !== "Decadal Mean")
            .map((year) =>
                year === `${dayjs().year()} (Forecast)`
                    ? dayjs().year()
                    : parseInt(year.toString()),
            );
        if (selectedYears.length === 0) {
            return [null, null];
        }
        const minYear = Math.min(...selectedYears);
        const maxYear = Math.max(...selectedYears);
        return [
            dayjs().year(minYear).startOf("year").toDate(),
            dayjs().year(maxYear).endOf("year").toDate(),
        ];
    }, [selectedWeatherYears]);

    const fieldWeather = useGetRequest<Weather[]>({
        url: `/fields/${field.id}/weather/`,
        params: {
            start_date: dayjs(startDate).format("YYYY-MM-DD"),
            end_date: dayjs(endDate).format("YYYY-MM-DD"),
        },
        enabled: startDate !== null && endDate !== null,
    });

    return (
        <div className="flex flex-row w-full justify-between gap-2">
            <div className="flex flex-col min-w-[200px]">
                <Listbox
                    aria-label="Weather Years"
                    items={yearOptions.map((year) => ({
                        id: year,
                        description: year.toString(),
                    }))}
                    selectionMode="multiple"
                    color="primary"
                    variant="flat"
                    className="border border-gray-300"
                    selectedKeys={selectedWeatherYears}
                    onSelectionChange={setSelectedWeatherYears}
                >
                    {(item) => (
                        <ListboxItem key={item.id!}>
                            {item.description}
                        </ListboxItem>
                    )}
                </Listbox>
            </div>
            {tenYearWeather.isLoading || fieldWeather.isLoading ? (
                <div className="flex flex-1 w-full h-72 justify-center items-center">
                    <Spinner size="lg" color="primary" />
                </div>
            ) : (
                <div className="flex flex-col w-full gap-2">
                    {Array.from(selectedWeatherYears).map((year) => (
                        <>
                            <div className="text-lg font-bold text-center text-primary-500">
                                {year}
                            </div>
                            <div className="flex flex-row w-full h-48 gap-4">
                                <ResponsiveContainer>
                                    <LineChart
                                        data={
                                            year === "Decadal Mean"
                                                ? tenYearWeather.data
                                                : fieldWeather.data?.filter(
                                                      (weather) =>
                                                          dayjs(weather.date)
                                                              .year()
                                                              .toString() ===
                                                          year,
                                                  )
                                        }
                                        margin={{
                                            top: 5,
                                            right: 20,
                                            left: 10,
                                            bottom: 5,
                                        }}
                                    >
                                        <XAxis
                                            dataKey="date"
                                            ticks={[
                                                `${year === "Decadal Mean" ? "2025" : year}-01-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-02-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-03-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-04-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-05-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-06-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-07-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-08-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-09-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-10-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-11-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-12-01`,
                                            ]}
                                            tickFormatter={(date) =>
                                                dayjs(date).format("MMM")
                                            }
                                        />
                                        <YAxis
                                            label={{
                                                value: `${t("temperature")} ${formatUnit("F", "C")}`,
                                                angle: -90,
                                                dx: -30,
                                            }}
                                            fontSize={12}
                                        />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            dataKey="max_temperature"
                                            stroke="#FF0000"
                                            name="Max Temperature"
                                            dot={false}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="min_temperature"
                                            stroke="#0000FF"
                                            name="Min Temperature"
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                                <ResponsiveContainer>
                                    <BarChart
                                        data={
                                            year === "Decadal Mean"
                                                ? tenYearWeather.data
                                                : fieldWeather.data?.filter(
                                                      (weather) =>
                                                          dayjs(weather.date)
                                                              .year()
                                                              .toString() ===
                                                          year,
                                                  )
                                        }
                                        margin={{
                                            top: 5,
                                            right: 20,
                                            left: 10,
                                            bottom: 5,
                                        }}
                                    >
                                        <XAxis
                                            dataKey="date"
                                            ticks={[
                                                `${year === "Decadal Mean" ? "2025" : year}-01-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-02-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-03-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-04-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-05-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-06-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-07-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-08-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-09-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-10-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-11-01`,
                                                `${year === "Decadal Mean" ? "2025" : year}-12-01`,
                                            ]}
                                            tickFormatter={(date) =>
                                                dayjs(date).format("MMM")
                                            }
                                        />
                                        <YAxis
                                            label={{
                                                value: `${t("chartLabels.precipitation")} (${formatUnit("in", "mm")})`,
                                                angle: -90,
                                                dx: -30,
                                            }}
                                            fontSize={12}
                                        />
                                        <Tooltip />
                                        <Bar
                                            dataKey="precipitation"
                                            fill="#00AAFF"
                                            name="Precipitation"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    ))}
                </div>
            )}
        </div>
    );
}

export default WeatherView;
